import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { useTranslation, useYupValidationResolver } from '@hooks';
import { brandsService } from '@services';
import { handleApiError } from '@helpers/api';
import { accountActions, alertActions, authActions, brandsActions } from '@actions';
import { FORM_TYPE, BackgroundType, ButtonType, FontType } from '@constants/brands';
import { Modal, Content, FormWrapper, PreviewWrapper, RadioButtonsWrapper, RadioButtons } from './styles';
import Settings from './Settings';
import Appearance from './Appearance';
import PreviewBrand from './PreviewBrand';

const Tab = {
    Settings: 0,
    Appearance: 1,
};

const BrandManageModal = ({ selectedBrand, modal, onClose, refetchCallback }) => {
    const [tab, setTab] = useState(Tab.Settings);

    const locations = useSelector(state => state.account?.account?.companies || []);
    const defaultBrandId = useSelector(state => state.brands.defaultBrandId);

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const BrandSchema = yup.object().shape({
        title: yup.string().required('validation.required'),
        locations: yup
            .array()
            .test('required', 'validation.required', value => (value || []).some(l => l.id?.length > 0)),
        backgroundColor: yup.string().required('validation.required').validateColor('validation.hexFormat'),
        fontColor: yup.string().required('validation.required').validateColor('validation.hexFormat'),
        buttonBackgroundColor: yup.string().required('validation.required').validateColor('validation.hexFormat'),
        buttonFontColor: yup.string().required('validation.required').validateColor('validation.hexFormat'),
    });

    const resolver = useYupValidationResolver(BrandSchema);

    const {
        getValues,
        setValue,
        control,
        watch,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            title: '',
            locations: [{ id: '' }],
            isDefault: false,
            backgroundStyle: BackgroundType.Solid,
            backgroundColor: '#8FA6AE',
            font: FontType.Rubik,
            fontColor: '#fff',
            buttonStyle: ButtonType['Softly Rounded'],
            buttonBackgroundColor: '#1F4C5C',
            buttonFontColor: '#fff',
            logo: null,
        },
    });

    useEffect(() => {
        if (!defaultBrandId) {
            dispatch(brandsActions.getDefault());
        }
    }, [defaultBrandId, dispatch]);

    useEffect(() => {
        if (modal.type !== FORM_TYPE.NEW) {
            setValue('title', selectedBrand.title, { shouldValidate: true });
            const assignedLocations = selectedBrand.locations.map(l => ({
                id: [{ id: `${l.id}`, label: locations.find(loc => loc.companyId === l.id)?.internalName }],
            }));
            setValue('locations', assignedLocations.length ? assignedLocations : [{ id: '' }], {
                shouldValidate: true,
            });
            const { appearance } = selectedBrand;
            setValue('backgroundStyle', appearance.backgroundStyle);
            setValue('backgroundColor', appearance.backgroundColor, { shouldValidate: true });
            setValue('font', appearance.font);
            setValue('fontColor', appearance.fontColor, { shouldValidate: true });
            setValue('buttonStyle', appearance.buttonStyle);
            setValue('buttonBackgroundColor', appearance.buttonBackgroundColor, { shouldValidate: true });
            setValue('buttonFontColor', appearance.buttonFontColor, { shouldValidate: true });
            setValue('logo', appearance.logo);
            setValue('isDefault', selectedBrand.isDefault, { shouldValidate: true });
        } else {
            setValue('isDefault', defaultBrandId ? false : true, { shouldValidate: true });
        }
    }, [selectedBrand, modal.type, setValue, defaultBrandId]);

    const RESPONSE_MSG = {
        [FORM_TYPE.EDIT]: {
            success: t('alertMessages.editSuccess'),
            error: t('alertMessages.editFail'),
        },
        [FORM_TYPE.NEW]: {
            success: t('alertMessages.createSuccess'),
            error: t('alertMessages.createFail'),
        },
    };

    const onSubmit = async () => {
        const values = getValues();
        try {
            await brandsService.addOrUpdateBrand({
                title: values.title,
                locations: values.locations.filter(l => l.id?.length).map(l => ({ id: +l.id[0]?.id })),
                appearance: {
                    backgroundStyle: values.backgroundStyle,
                    backgroundColor: values.backgroundColor,
                    font: values.font,
                    fontColor: values.fontColor,
                    buttonStyle: values.buttonStyle,
                    buttonBackgroundColor: values.buttonBackgroundColor,
                    buttonFontColor: values.buttonFontColor,
                    logo: values.logo,
                },
                isDefault: values.isDefault,
                ...(modal.type === FORM_TYPE.EDIT && { id: selectedBrand.id }),
            });
        } catch (error) {
            handleApiError(error, RESPONSE_MSG[modal.type].error);
            return;
        }

        dispatch(alertActions.success(RESPONSE_MSG[modal.type].success));
        refetchCallback();
        dispatch(accountActions.get());
        dispatch(authActions.getCurrentUser());
        dispatch(brandsActions.getDefault());
        onClose();
    };

    return (
        <Modal
            fullScreen
            isOpen={modal.open}
            handleClose={onClose}
            isDirty={isDirty}
            title={modal.type === FORM_TYPE.NEW ? t('buttons.createBrand') : t('SettingsBrands.editBrand')}
            onPrimaryAction={handleSubmit(onSubmit)}
            primaryActionText={t('buttons.save')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty}
        >
            <FormProvider {...{ control, setValue, getValues, watch }}>
                <FormWrapper>
                    <RadioButtonsWrapper>
                        <RadioButtons
                            value={tab}
                            onChange={setTab}
                            options={[
                                { value: Tab.Settings, label: t('common.settings') },
                                { value: Tab.Appearance, label: t('common.appearance') },
                            ]}
                        />
                    </RadioButtonsWrapper>
                    <Content>
                        {tab === Tab.Settings && (
                            <Settings
                                locations={locations}
                                modal={modal}
                                selectedBrand={selectedBrand}
                                defaultBrandId={defaultBrandId}
                            />
                        )}
                        {tab === Tab.Appearance && <Appearance />}
                    </Content>
                </FormWrapper>
                <PreviewWrapper>
                    <PreviewBrand locations={locations} />
                </PreviewWrapper>
            </FormProvider>
        </Modal>
    );
};

export default BrandManageModal;
